import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search,
  Download,
  ChevronRight,
  ArrowUpRight,
  Ship,
  X,
  FileText,
  Columns3,
  ChevronUp,
  ChevronDown,
  RotateCcw,
} from 'lucide-react';

import {
  fmtDate,
  etaLabel,
  type Company,
} from '@/features/dashboard/data/claudeMockData';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDirection } from '@/contexts/DirectionContext';
import {
  ALL_CONTAINER_STATUSES,
  containerStatusLabel,
  statusFilterOptions,
} from '@/features/containers/constants/statuses';
import {
  formatEtaHeading,
  formatEtaSubtext,
} from '@/features/containers/constants/directionLabels';
import { RouteCell, CompanyBadge } from '@/features/dashboard/components/claude/shared';
import type { BillRow } from '../interfaces/bill-row.interface';
import { useBillRows } from '../hooks/useBillRows';
import { BillPreviewModal } from '../components/BillPreviewModal';
import { ClientNoteEditor } from '@/components/claude/ClientNoteEditor';
import { useSaveClientNote } from '@/hooks/useClientNote';
import { exportXlsx, type ExcelColumn } from '@/utils/exportXlsx';
import { formatQuotedOceanFreight } from '@/utils/oceanFreight';
import {
  type BillColumnId,
  type BillColumnState,
  type BillsSortDir,
  type BillsSortKey,
  billColumnLabel,
  defaultBillColumns,
  loadBillsPrefs,
  moveBillColumn,
  saveBillsPrefs,
  toggleBillColumnVisible,
} from '../utils/billsTablePrefs';

/**
 * Construiește un Company sintetic pentru badge folosind datele venite din
 * backend (companyName/companyId).
 */
function buildCompanyForBadge(name: string, id: number): Company | undefined {
  if (!name) return undefined;
  const palette = ['#1e6bd6', '#0c8a5b', '#b6500f', '#7a3aa8', '#0f7a8a', '#c0392b'];
  const color = palette[id % palette.length] || palette[0];
  const short =
    name
      .split(/\s+/)
      .map((p) => p.charAt(0).toUpperCase())
      .join('')
      .slice(0, 3) || name.slice(0, 3).toUpperCase();
  return { id: `co_${id}`, name, short, color, vat: '' };
}

const SortHeader: React.FC<{
  id: BillsSortKey;
  label: string;
  sortKey: BillsSortKey;
  sortDir: BillsSortDir;
  onSort: (k: BillsSortKey) => void;
  align?: 'right';
  style?: React.CSSProperties;
}> = ({ id, label, sortKey, sortDir, onSort, align, style }) => {
  const isSorted = sortKey === id;
  return (
    <th
      className={'sortable' + (isSorted ? ' sorted' : '')}
      onClick={() => onSort(id)}
      style={{
        ...(align === 'right' ? { textAlign: 'right' as const } : {}),
        ...style,
      }}
    >
      {label}
      <span className="sort-ind">{isSorted ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>
    </th>
  );
};

const sortValue = (row: BillRow, key: BillsSortKey): string | number => {
  switch (key) {
    case 'no':
      return (row.code || '').toLowerCase();
    case 'company':
      return (row.companyName || '').toLowerCase();
    case 'shipper':
      return (row.shipperName || '').toLowerCase();
    case 'consignee':
      return (row.consigneeName || '').toLowerCase();
    case 'route':
      return `${row.pol || ''}|${row.pod || ''}`.toLowerCase();
    case 'vessel':
      return (row.vesselName || '').toLowerCase();
    case 'motherVessel':
      return (row.motherVesselName || '').toLowerCase();
    case 'ts1':
      return (row.tsPort1Name || row.tsPort1 || '').toLowerCase();
    case 'ts2':
      return (row.tsPort2Name || row.tsPort2 || '').toLowerCase();
    case 'containers':
      return row.containerCount ?? 0;
    case 'issued':
      return row.issued ? new Date(row.issued).getTime() : Number.POSITIVE_INFINITY;
    case 'onBoard':
      return row.onBoardDate
        ? new Date(row.onBoardDate).getTime()
        : Number.POSITIVE_INFINITY;
    case 'eta':
      return row.eta ? new Date(row.eta).getTime() : Number.POSITIVE_INFINITY;
    case 'type':
      return (row.shippingType || '').toLowerCase();
    case 'oceanFreight':
      return typeof row.oceanFreightAmount === 'number' && Number.isFinite(row.oceanFreightAmount)
        ? row.oceanFreightAmount
        : Number.POSITIVE_INFINITY;
    case 'status':
      return row.status;
    case 'note':
      return (row.clientNote || '').toLowerCase();
    default:
      return '';
  }
};

const BILL_EXCEL_WIDTH: Record<BillColumnId, number> = {
  no: 20,
  company: 26,
  shipper: 28,
  consignee: 28,
  route: 16,
  vessel: 24,
  motherVessel: 24,
  ts1: 18,
  ts2: 18,
  containers: 10,
  issued: 12,
  onBoard: 14,
  eta: 18,
  type: 8,
  oceanFreight: 18,
  status: 18,
  note: 28,
};

/**
 * Valoare Excel per coloană a tabelului. Booking-ul (afișat sub codul B/L în UI)
 * e coloană separată în Excel — vezi `billExtraExcelColumns`.
 */
function billExcelValue(
  id: BillColumnId,
  r: BillRow,
  lang: 'ro' | 'en',
): string | number | Date | null {
  switch (id) {
    case 'no':
      return r.code || '';
    case 'company':
      return r.companyName || '';
    case 'shipper':
      return r.shipperName || '';
    case 'consignee':
      return r.consigneeName || '';
    case 'route':
      return r.pol || r.pod ? `${r.pol || '—'} → ${r.pod || '—'}` : '';
    case 'vessel':
      return [r.vesselName, r.vesselCode].filter(Boolean).join(' · ');
    case 'motherVessel':
      return [r.motherVesselName, r.motherVesselCode].filter(Boolean).join(' · ');
    case 'ts1':
      return [r.tsPort1, r.tsPort1Name].filter(Boolean).join(' · ');
    case 'ts2':
      return [r.tsPort2, r.tsPort2Name].filter(Boolean).join(' · ');
    case 'containers':
      return r.containerCount ?? 0;
    case 'issued':
      return r.issued ? new Date(r.issued) : null;
    case 'onBoard':
      return r.onBoardDate ? new Date(r.onBoardDate) : null;
    case 'eta':
      return r.eta ? new Date(r.eta) : null;
    case 'type':
      return r.shippingType || '';
    case 'oceanFreight':
      return formatQuotedOceanFreight(r, lang);
    case 'status':
      return containerStatusLabel(r.status, lang, r.rawStatus || r.status);
    case 'note':
      return r.clientNote || '';
    default:
      return '';
  }
}

/**
 * Coloane suplimentare în Excel, care în tabel apar doar ca text secundar
 * sau deloc: booking, marfă, POL/POD, port ETA.
 */
function billExtraExcelColumns(lang: 'ro' | 'en'): ExcelColumn<BillRow>[] {
  const isRo = lang === 'ro';
  return [
    { header: 'Booking', value: (r) => r.bookingNumber || '', width: 14 },
    { header: isRo ? 'Marfă' : 'Commodity', value: (r) => r.commodity || '', width: 32 },
    {
      header: isRo ? 'Marfă (tradusă)' : 'Commodity (translated)',
      value: (r) => r.translatedCommodity || '',
      width: 32,
    },
    { header: 'POL', value: (r) => r.pol || '', width: 8 },
    { header: 'POD', value: (r) => r.pod || '', width: 8 },
    {
      header: isRo ? 'Port ETA' : 'ETA port',
      value: (r) => [r.etaPort, r.etaPortName].filter(Boolean).join(' · '),
      width: 22,
    },
  ];
}

const SORTABLE: Set<BillColumnId> = new Set([
  'no',
  'company',
  'shipper',
  'consignee',
  'route',
  'vessel',
  'motherVessel',
  'ts1',
  'ts2',
  'containers',
  'issued',
  'onBoard',
  'eta',
  'type',
  'oceanFreight',
  'status',
  'note',
]);

/** Poziția navei pe linia din cardul de telefon: estimată din On Board → ETA (rândul n-are progres). */
function rowProgress(b: BillRow): number {
  if (['discharged', 'in_delivery', 'completed'].includes(b.status)) return 1;
  if (!b.onBoardDate || !b.eta) return 0.5;
  const from = new Date(b.onBoardDate).getTime();
  const to = new Date(b.eta).getTime();
  if (!(to > from)) return 0.5;
  return Math.min(0.97, Math.max(0.03, (Date.now() - from) / (to - from)));
}

const BillsIndex: React.FC = () => {
  const { currentLanguage: lang } = useLanguage();
  const { direction } = useDirection();
  const isRo = lang === 'ro';
  const isExport = direction === 'export';
  const dirKey = direction === 'export' ? 'export' : 'import';

  const { data, isLoading } = useBillRows();
  const { saveBill } = useSaveClientNote();
  const rows: BillRow[] = data || [];

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');
  const [sortKey, setSortKey] = useState<BillsSortKey>('onBoard');
  const [sortDir, setSortDir] = useState<BillsSortDir>('desc');
  const [selected, setSelected] = useState<BillRow | null>(null);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [columns, setColumns] = useState<BillColumnState[]>(() =>
    defaultBillColumns(direction === 'export' ? 'export' : 'import'),
  );
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const colMenuRef = useRef<HTMLDivElement>(null);
  const prefsHydrated = useRef(false);
  const skipPageReset = useRef(true);

  useEffect(() => {
    document.body.classList.add('dashboard-claude-bg');
    return () => {
      document.body.classList.remove('dashboard-claude-bg');
    };
  }, []);

  // Preferințe per direcție (import / export separat).
  useEffect(() => {
    const p = loadBillsPrefs(dirKey);
    setStatusFilter(p.statusFilter);
    setSearch(p.search);
    setSortKey(p.sortKey);
    setSortDir(p.sortDir);
    setPage(p.page);
    setPageSize(p.pageSize);
    setColumns(p.columns);
    prefsHydrated.current = true;
    skipPageReset.current = true;
    setColMenuOpen(false);
  }, [dirKey]);

  useEffect(() => {
    if (!prefsHydrated.current) return;
    saveBillsPrefs(dirKey, {
      statusFilter,
      search,
      sortKey,
      sortDir,
      page,
      pageSize,
      columns,
    });
  }, [dirKey, statusFilter, search, sortKey, sortDir, page, pageSize, columns]);

  useEffect(() => {
    if (!colMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target as Node)) {
        setColMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setColMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [colMenuOpen]);

  // Deep-link `?focus=<id>` (vine din command palette).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const focus = searchParams.get('focus');
    if (!focus || rows.length === 0) return;
    const id = Number(focus);
    if (!Number.isFinite(id)) return;
    const target = rows.find((r) => r.id === id);
    if (target) {
      setSelected(target);
      const next = new URLSearchParams(searchParams);
      next.delete('focus');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, rows]);

  const onSort = (k: BillsSortKey) => {
    if (sortKey === k) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      // Emis / OBD: recente primele; ETA: apropiate; rest ASC.
      if (k === 'issued' || k === 'onBoard') setSortDir('desc');
      else setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    let arr = rows;

    if (statusFilter !== 'all') {
      arr = arr.filter((r) => r.status === statusFilter);
    }

    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter((r) =>
        [
          r.code,
          r.bookingNumber,
          r.shipperName,
          r.consigneeName,
          r.vesselName,
          r.vesselCode,
          r.motherVesselName,
          r.tsPort1,
          r.tsPort2,
          r.commodity,
          r.translatedCommodity,
          r.companyName,
          r.pol,
          r.pod,
          r.clientNote,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q)),
      );
    }

    return [...arr].sort((a, b) => {
      const va = sortValue(a, sortKey);
      const vb = sortValue(b, sortKey);
      if (typeof va === 'number' && typeof vb === 'number') {
        if (va === Number.POSITIVE_INFINITY && vb === Number.POSITIVE_INFINITY) return 0;
        if (va === Number.POSITIVE_INFINITY) return 1;
        if (vb === Number.POSITIVE_INFINITY) return -1;
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, statusFilter, search, sortKey, sortDir]);

  useEffect(() => {
    if (skipPageReset.current) {
      skipPageReset.current = false;
      return;
    }
    setPage(1);
  }, [statusFilter, search, sortKey, sortDir, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const paginated = filtered.slice(pageStart, pageStart + pageSize);
  const showingFrom = filtered.length === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(filtered.length, pageStart + pageSize);

  const visibleColumns = useMemo(() => columns.filter((c) => c.visible), [columns]);
  const colSpan = visibleColumns.length + 1;

  const etaHeading = isExport ? formatEtaHeading(direction, lang) : 'ETA';

  // Export Excel: TOATE coloanele tabelului (și cele ascunse), în ordinea
  // configurată, plus coloanele suplimentare (booking, marfă, POL/POD, port ETA).
  // Rândurile = ce e filtrat/căutat curent, toate paginile.
  const handleExport = () => {
    if (filtered.length === 0) return;
    const today = new Date().toISOString().slice(0, 10);
    const tableColumns: ExcelColumn<BillRow>[] = columns.map((col) => ({
      header: billColumnLabel(col.id, lang, etaHeading, dirKey),
      value: (r) => billExcelValue(col.id, r, lang),
      width: BILL_EXCEL_WIDTH[col.id],
    }));
    exportXlsx<BillRow>({
      fileName: `conosamente-${isExport ? 'export' : 'import'}-${today}`,
      sheetName: isRo ? 'Conosamente' : 'Bills of Lading',
      rows: filtered,
      columns: [...tableColumns, ...billExtraExcelColumns(lang)],
    });
  };

  const statusOptions = statusFilterOptions(direction);

  useEffect(() => {
    if (statusFilter === 'all') return;
    if (statusOptions.some((o) => o.id === statusFilter)) return;
    setStatusFilter('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction]);

  const truncate = (s: string | null | undefined, max = 30): string => {
    if (!s) return '';
    return s.length > max ? s.slice(0, max) + '...' : s;
  };

  const resetColumns = useCallback(() => {
    setColumns(defaultBillColumns(dirKey));
  }, [dirKey]);

  const renderHeaderCell = (col: BillColumnState) => {
    const label = billColumnLabel(col.id, lang, etaHeading, dirKey);
    if (SORTABLE.has(col.id)) {
      return (
        <SortHeader
          key={col.id}
          id={col.id as BillsSortKey}
          label={label}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={onSort}
          align={col.id === 'containers' || col.id === 'oceanFreight' ? 'right' : undefined}
          style={col.id === 'eta' ? { minWidth: 132 } : undefined}
        />
      );
    }
    return (
      <th
        key={col.id}
        style={
          col.id === 'containers' || col.id === 'oceanFreight'
            ? { textAlign: 'right' }
            : undefined
        }
      >
        {label}
      </th>
    );
  };

  const renderBodyCell = (col: BillColumnState, b: BillRow) => {
    const st = ALL_CONTAINER_STATUSES[b.status];
    const co = buildCompanyForBadge(b.companyName, b.companyId);
    switch (col.id) {
      case 'no':
        return (
          <td key={col.id}>
            <div className="mono fw-600 fs-13">
              <span className="ctn-no">
                {b.code || '—'}
                <ArrowUpRight className="ctn-no-arrow" size={13} strokeWidth={2.4} />
              </span>
            </div>
            <div className="muted fs-11 mono mt-1">{b.bookingNumber || ''}</div>
          </td>
        );
      case 'company':
        return (
          <td key={col.id}>
            <CompanyBadge company={co} />
          </td>
        );
      case 'shipper':
        return (
          <td key={col.id}>
            <div
              className="fs-13 fw-500"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 180,
              }}
              title={b.shipperName || ''}
            >
              {truncate(b.shipperName, 30) || '—'}
            </div>
          </td>
        );
      case 'consignee':
        return (
          <td key={col.id}>
            <div
              className="fs-13"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 180,
              }}
              title={b.consigneeName || ''}
            >
              {truncate(b.consigneeName, 30) || '—'}
            </div>
          </td>
        );
      case 'route':
        return (
          <td key={col.id}>
            {b.pol && b.pod ? (
              <RouteCell pol={b.pol} pod={b.pod} />
            ) : (
              <span className="muted fs-12">—</span>
            )}
          </td>
        );
      case 'vessel':
        return (
          <td key={col.id}>
            <div className="fw-500 fs-13" style={{ whiteSpace: 'nowrap' }}>
              {b.vesselName || '—'}
            </div>
            <div className="muted fs-11 mt-1 mono">{b.vesselCode || ''}</div>
          </td>
        );
      case 'motherVessel':
        return (
          <td key={col.id}>
            <div className="fw-500 fs-13" style={{ whiteSpace: 'nowrap' }}>
              {b.motherVesselName || '—'}
            </div>
            <div className="muted fs-11 mt-1 mono">{b.motherVesselCode || ''}</div>
          </td>
        );
      case 'ts1':
        return (
          <td key={col.id}>
            <div className="mono fw-500 fs-13">{b.tsPort1 || '—'}</div>
            {b.tsPort1Name ? <div className="muted fs-11 mt-1">{b.tsPort1Name}</div> : null}
          </td>
        );
      case 'ts2':
        return (
          <td key={col.id}>
            <div className="mono fw-500 fs-13">{b.tsPort2 || '—'}</div>
            {b.tsPort2Name ? <div className="muted fs-11 mt-1">{b.tsPort2Name}</div> : null}
          </td>
        );
      case 'containers':
        return (
          <td key={col.id} style={{ textAlign: 'right' }} className="mono fw-600">
            {b.containerCount}
          </td>
        );
      case 'issued':
        return (
          <td key={col.id} className="muted fs-13" style={{ whiteSpace: 'nowrap' }}>
            {b.issued ? fmtDate(b.issued, lang) : '—'}
          </td>
        );
      case 'onBoard':
        return (
          <td key={col.id} className="muted fs-13" style={{ whiteSpace: 'nowrap' }}>
            {b.onBoardDate ? fmtDate(b.onBoardDate, lang) : '—'}
          </td>
        );
      case 'eta':
        return (
          <td key={col.id} className="fs-13" style={{ minWidth: 132, whiteSpace: 'nowrap' }}>
            <div>{b.eta ? fmtDate(b.eta, lang) : '—'}</div>
            {b.eta && isExport ? (
              <div className="muted fs-11">
                {formatEtaSubtext(direction, lang, b) ||
                  formatEtaHeading(direction, lang, b)}
              </div>
            ) : null}
          </td>
        );
      case 'type':
        return (
          <td key={col.id}>
            {b.shippingType ? (
              <span className="badge badge-neutral" style={{ fontSize: 10.5 }}>
                {b.shippingType}
              </span>
            ) : (
              <span className="muted fs-12">—</span>
            )}
          </td>
        );
      case 'oceanFreight': {
        const text = formatQuotedOceanFreight(b, lang);
        const isAmount = typeof b.oceanFreightAmount === 'number';
        return (
          <td key={col.id} style={{ textAlign: 'right' }}>
            {text === '—' ? (
              <span className="muted">—</span>
            ) : (
              <span className={isAmount ? 'mono fw-600 fs-13' : 'muted fs-12'}>{text}</span>
            )}
          </td>
        );
      }
      case 'status':
        return (
          <td key={col.id}>
            {st ? (
              <span className={'badge ' + st.badge}>
                <span className="dot" />
                {containerStatusLabel(b.status, lang)}
              </span>
            ) : (
              <span className="muted fs-11">{b.rawStatus || b.status}</span>
            )}
          </td>
        );
      case 'note':
        return (
          <td
            key={col.id}
            className="client-note-cell"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <ClientNoteEditor
              value={b.clientNote}
              onSave={(next) => saveBill(b.id, next).then(() => undefined)}
              variant="cell"
              lang={lang}
            />
          </td>
        );
      default:
        return <td key={col.id}>—</td>;
    }
  };

  const renderEmpty = (span: number) => (
    <tr>
      <td colSpan={span}>
        <div className="empty">
          <div className="icon">
            <FileText size={22} strokeWidth={2} />
          </div>
          <div className="fw-600">
            {isLoading
              ? isRo
                ? 'Se încarcă…'
                : 'Loading…'
              : isRo
                ? 'Niciun conosament'
                : 'No bills'}
          </div>
          {!isLoading && (
            <div className="muted fs-12 mt-1">
              {isRo
                ? 'Încercați să schimbați filtrele sau căutarea.'
                : 'Try changing filters or search.'}
            </div>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <div style={{ color: 'var(--text)' }}>
      <div className="ctn-page-head">
        <h1>
          {isRo ? 'Conosamente' : 'Bills of Lading'}
          <span className="ctn-count">
            {filtered.length} {isRo ? 'documente' : 'documents'}
          </span>
        </h1>
        <div className="muted fs-13 mt-1 hidden-mobile">
          {isRo
            ? isExport
              ? 'Conosamentele bookingurilor tale de export, sortabile și filtrabile după status.'
              : 'Conosamentele aferente bookingurilor tale, sortabile și filtrabile după status.'
            : isExport
              ? 'Bills of lading for your export bookings, sortable and filterable by status.'
              : 'Bills of lading for your bookings, sortable and filterable by status.'}
        </div>
      </div>

      <div className="ctn-toolbar">
        <div className="ctn-tb-row">
          <div className="search-input ctn-search">
            <Search size={15} />
            <input
              type="search"
              inputMode="search"
              enterKeyHint="search"
              placeholder={
                isExport
                  ? isRo
                    ? 'B/L, booking, navă…'
                    : 'B/L, booking, vessel…'
                  : isRo
                    ? 'B/L, booking, shipper, navă…'
                    : 'B/L, booking, shipper, vessel…'
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={isRo ? 'Caută conosamente' : 'Search bills of lading'}
            />
            {search && (
              <button
                type="button"
                className="ctn-search-clear"
                onClick={() => setSearch('')}
                aria-label={isRo ? 'Șterge căutarea' : 'Clear search'}
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="ctn-tb-actions">
        <div className="col-config-wrap hidden-mobile" ref={colMenuRef}>
          <button
            type="button"
            className={'btn btn-sm' + (colMenuOpen ? ' active' : '')}
            onClick={() => setColMenuOpen((o) => !o)}
            title={isRo ? 'Configurează coloanele' : 'Configure columns'}
            aria-expanded={colMenuOpen}
          >
            <Columns3 size={13} />
            {isRo ? 'Coloane' : 'Columns'}
          </button>
          {colMenuOpen && (
            <div className="dropdown col-config-menu" role="menu">
              <div className="col-config-title">
                {isRo
                  ? `Coloane (${isExport ? 'export' : 'import'})`
                  : `Columns (${isExport ? 'export' : 'import'})`}
              </div>
              {columns.map((col, idx) => (
                <div key={col.id} className="col-config-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={col.visible}
                      onChange={() =>
                        setColumns((prev) => toggleBillColumnVisible(prev, col.id))
                      }
                    />
                    <span className="col-name">
                      {billColumnLabel(col.id, lang, etaHeading, dirKey)}
                    </span>
                  </label>
                  <div className="col-config-move">
                    <button
                      type="button"
                      disabled={idx === 0}
                      onClick={() => setColumns((prev) => moveBillColumn(prev, idx, -1))}
                      aria-label={isRo ? 'Mută în sus' : 'Move up'}
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      type="button"
                      disabled={idx === columns.length - 1}
                      onClick={() => setColumns((prev) => moveBillColumn(prev, idx, 1))}
                      aria-label={isRo ? 'Mută în jos' : 'Move down'}
                    >
                      <ChevronDown size={14} />
                    </button>
                  </div>
                </div>
              ))}
              <div className="col-config-footer">
                <button type="button" className="btn btn-sm" onClick={resetColumns}>
                  <RotateCcw size={12} />
                  {isRo ? 'Reset' : 'Reset'}
                </button>
              </div>
            </div>
          )}
        </div>
        <button
          type="button"
          className="btn btn-sm ctn-export"
          onClick={handleExport}
          disabled={filtered.length === 0}
          aria-label={isRo ? 'Exportă în Excel' : 'Export to Excel'}
          title={
            filtered.length === 0
              ? isRo
                ? 'Nimic de exportat'
                : 'Nothing to export'
              : isRo
                ? 'Exportă lista în Excel'
                : 'Export the list to Excel'
          }
        >
          <Download size={14} />
          <span className="ctn-export-label">Export</span>
        </button>
          </div>
        </div>

        <div className="ctn-chips" role="tablist" aria-label={isRo ? 'Filtrează după status' : 'Filter by status'}>
          {statusOptions.map((s) => {
            const count =
              s.id === 'all' ? rows.length : rows.filter((r) => r.status === s.id).length;
            const active = statusFilter === s.id;
            return (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={'ctn-chip' + (active ? ' active' : '') + (count === 0 ? ' is-empty' : '')}
                onClick={() => setStatusFilter(s.id)}
              >
                {isRo ? s.labelRo : s.labelEn}
                <span className="ctn-chip-count">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop table */}
      <div className="card hidden-mobile">
        <div
          className="table-wrap sticky-thead"
          style={{ maxHeight: 'calc(100vh - 290px)', overflowY: 'auto' }}
        >
          <table className="data compact ctn-table">
            <thead>
              <tr>
                {visibleColumns.map(renderHeaderCell)}
                <th style={{ width: 30 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && renderEmpty(colSpan)}
              {paginated.map((b) => (
                <tr
                  key={`${b.bookingId}-${b.id}`}
                  className="clickable"
                  onClick={() => setSelected(b)}
                >
                  {visibleColumns.map((col) => renderBodyCell(col, b))}
                  <td>
                    <ChevronRight size={16} strokeWidth={2} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="show-mobile">
        {filtered.length === 0 && (
          <div className="card" style={{ padding: 0 }}>
            <div className="empty">
              <div className="icon">
                <FileText size={22} strokeWidth={2} />
              </div>
              <div className="fw-600">
                {isLoading
                  ? isRo
                    ? 'Se încarcă…'
                    : 'Loading…'
                  : isRo
                    ? 'Niciun conosament'
                    : 'No bills'}
              </div>
            </div>
          </div>
        )}
        {paginated.map((b) => {
          const st = ALL_CONTAINER_STATUSES[b.status];
          const co = buildCompanyForBadge(b.companyName, b.companyId);
          return (
            <div
              key={`m-${b.bookingId}-${b.id}`}
              className="card ctn-mcard"
              role="button"
              tabIndex={0}
              onClick={() => setSelected(b)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelected(b);
                }
              }}
            >
              <div className="mc-head">
                <div style={{ minWidth: 0 }}>
                  <div className="mc-no mono">
                    {b.code || '—'}
                    <ArrowUpRight size={14} strokeWidth={2.4} />
                  </div>
                  <div className="mc-desc">
                    {!isExport && b.shippingType && <span className="mc-type">{b.shippingType}</span>}
                    <span className="mc-type">
                      {b.containerCount}{' '}
                      {isRo
                        ? b.containerCount === 1
                          ? 'container'
                          : 'containere'
                        : b.containerCount === 1
                          ? 'container'
                          : 'containers'}
                    </span>
                    {b.bookingNumber && b.bookingNumber !== b.code ? (
                      <span>
                        Booking <span className="mono">{b.bookingNumber}</span>
                      </span>
                    ) : null}
                  </div>
                </div>
                {st ? (
                  <span className={'badge ' + st.badge}>
                    <span className="dot" />
                    {containerStatusLabel(b.status, lang)}
                  </span>
                ) : (
                  <span className="muted fs-11">{b.rawStatus || b.status}</span>
                )}
              </div>

              <div className="mc-route">
                <span className="mc-port mono">{b.pol || '—'}</span>
                <span
                  className="mc-line"
                  style={{ '--mc-progress': `${Math.round(rowProgress(b) * 100)}%` } as React.CSSProperties}
                >
                  <span className="mc-ship">
                    <Ship size={12} strokeWidth={2.2} />
                  </span>
                </span>
                <span className="mc-port mono">{b.pod || '—'}</span>
                <span className="mc-vessel">
                  {b.vesselName || (isRo ? 'Navă neanunțată' : 'Vessel not announced')}
                  {b.vesselCode && <span className="mono">{b.vesselCode}</span>}
                  {isExport && (b.tsPort1 || b.tsPort2) && (
                    <span className="mono">via {[b.tsPort1, b.tsPort2].filter(Boolean).join(', ')}</span>
                  )}
                </span>
              </div>

              <div className="mc-facts">
                <div className="mc-fact">
                  <span className="k">{isExport ? formatEtaHeading(direction, lang, b) : 'ETA'}</span>
                  <span className="v">{b.eta ? fmtDate(b.eta, lang) : '—'}</span>
                  {b.eta && (
                    <span className={'s' + (new Date(b.eta).getTime() < Date.now() - 86_400_000 ? ' past' : '')}>
                      {isExport ? formatEtaSubtext(direction, lang, b) || '' : etaLabel(b.eta, lang)}
                    </span>
                  )}
                </div>
                <div className="mc-fact">
                  <span className="k">On Board</span>
                  <span className="v">{b.onBoardDate ? fmtDate(b.onBoardDate, lang) : '—'}</span>
                </div>
                {isExport ? (
                  <div className="mc-fact">
                    <span className="k">{isRo ? 'Navă mamă' : 'Mother vessel'}</span>
                    <span className="v">{b.motherVesselName || '—'}</span>
                  </div>
                ) : (
                  <div className="mc-fact">
                    <span className="k">{isRo ? 'Navlu' : 'Freight'}</span>
                    <span className={'v' + (typeof b.oceanFreightAmount === 'number' ? ' mono' : ' muted')}>
                      {formatQuotedOceanFreight(b, lang)}
                    </span>
                  </div>
                )}
              </div>

              <div className="mc-foot">
                <CompanyBadge company={co} />
                {b.shipperName ? (
                  <span className="mc-shipper" title={b.shipperName}>
                    {b.shipperName}
                  </span>
                ) : null}
              </div>

              <div
                className="mc-note"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <ClientNoteEditor
                  value={b.clientNote}
                  onSave={(next) => saveBill(b.id, next).then(() => undefined)}
                  variant="cell"
                  lang={lang}
                />
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length > 0 && (
        <div
          className="row gap-2"
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 14,
            flexWrap: 'wrap',
          }}
        >
          <div className="muted fs-12">
            {isRo
              ? `Afișare ${showingFrom}–${showingTo} din ${filtered.length}`
              : `Showing ${showingFrom}–${showingTo} of ${filtered.length}`}
          </div>
          <div className="row gap-2 pager-row" style={{ alignItems: 'center' }}>
            <span className="muted fs-12">{isRo ? 'Pe pagină' : 'Per page'}</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              style={{
                background: 'var(--surface)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-md)',
                padding: '4px 8px',
                fontSize: 12.5,
                cursor: 'pointer',
              }}
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-sm"
              disabled={currentPage <= 1}
              onClick={() => setPage(1)}
              style={{ opacity: currentPage <= 1 ? 0.4 : 1 }}
            >
              «
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={currentPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              style={{ opacity: currentPage <= 1 ? 0.4 : 1 }}
            >
              ‹ {isRo ? 'Anterior' : 'Prev'}
            </button>
            <span className="fs-12 pager-count" style={{ minWidth: 80, textAlign: 'center' }}>
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-sm"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              style={{ opacity: currentPage >= totalPages ? 0.4 : 1 }}
            >
              {isRo ? 'Următor' : 'Next'} ›
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={currentPage >= totalPages}
              onClick={() => setPage(totalPages)}
              style={{ opacity: currentPage >= totalPages ? 0.4 : 1 }}
            >
              »
            </button>
          </div>
        </div>
      )}

      <BillPreviewModal
        isOpen={selected !== null}
        bill={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
};

export default BillsIndex;
